import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2, Search, Users, Globe, Pencil, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;

// Reusable read-only-by-default table for the two Account Creation Master
// collections (gender + country). A row must be explicitly put into edit
// mode via the pencil button before any field is typable — prevents
// accidental keystrokes overwriting seed values.
function MasterTable({ endpoint, columns, uniqKey, testIdPrefix, newRowShape }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);   // only one row editable at a time
  const [draft, setDraft] = useState(null);           // {...row} being edited
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/account-master/${endpoint}`, { headers: authHeader });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(`Failed to load ${endpoint}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({ ...row });
  };
  const cancelEdit = () => {
    // If the row was a "new-…" placeholder, drop it entirely on cancel.
    if (editingId && String(editingId).startsWith("new-")) {
      setRows((p) => p.filter((r) => r.id !== editingId));
    }
    setEditingId(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving((p) => ({ ...p, [draft.id]: true }));
    try {
      await axios.post(`${API}/api/account-master/${endpoint}`, draft, { headers: authHeader });
      toast.success(`${draft[uniqKey]} saved`);
      setEditingId(null);
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || e.message}`);
    } finally {
      setSaving((p) => ({ ...p, [draft.id]: false }));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row[uniqKey]}? This cannot be undone.`)) return;
    setDeleting((p) => ({ ...p, [row.id]: true }));
    try {
      await axios.delete(`${API}/api/account-master/${endpoint}/${row[uniqKey]}`, { headers: authHeader });
      toast.success(`${row[uniqKey]} removed`);
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeleting((p) => ({ ...p, [row.id]: false }));
    }
  };

  const addNew = () => {
    const tmpId = `new-${Date.now()}`;
    const newRow = { id: tmpId, ...newRowShape };
    setRows((p) => [newRow, ...p]);
    setEditingId(tmpId);
    setDraft({ ...newRow });
  };

  const visible = rows.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid={`${testIdPrefix}-search`}
            placeholder={`Search ${endpoint}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          data-testid={`${testIdPrefix}-add-btn`}
          onClick={addNew}
          disabled={!!editingId}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add {endpoint}
        </Button>
        <span className="text-xs text-slate-400 ml-auto">
          {loading ? "Loading…" : `${visible.length} of ${rows.length} rows`}
        </span>
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-2 font-medium">{c.label}</th>
              ))}
              <th className="text-right px-4 py-2 font-medium w-40">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((r) => {
              const isEditing = editingId === r.id;
              return (
                <tr
                  key={r.id}
                  className={isEditing ? "bg-amber-50" : "hover:bg-slate-50"}
                  data-testid={`${testIdPrefix}-row-${r[uniqKey] || r.id}`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-2">
                      {isEditing ? (
                        <Input
                          data-testid={`${testIdPrefix}-input-${c.key}-${r[uniqKey] || r.id}`}
                          value={draft?.[c.key] ?? ""}
                          onChange={(e) => setDraft((d) => ({
                            ...d,
                            [c.key]: c.type === "number" ? Number(e.target.value) : e.target.value,
                          }))}
                          placeholder={c.placeholder}
                          type={c.type === "number" ? "number" : "text"}
                          maxLength={c.maxLength}
                          className="h-8 text-sm"
                          autoFocus={c.key === columns[0].key}
                        />
                      ) : (
                        <span className="text-slate-800" data-testid={`${testIdPrefix}-cell-${c.key}-${r[uniqKey] || r.id}`}>
                          {r[c.key] ?? ""}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right space-x-1">
                    {isEditing ? (
                      <>
                        <Button
                          size="sm" variant="outline"
                          disabled={saving[r.id]}
                          onClick={save}
                          data-testid={`${testIdPrefix}-save-${r[uniqKey] || r.id}`}
                          className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        >
                          {saving[r.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />Save</>}
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={cancelEdit}
                          data-testid={`${testIdPrefix}-cancel-${r[uniqKey] || r.id}`}
                          className="h-8 text-slate-600 border-slate-200 hover:bg-slate-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm" variant="outline"
                          disabled={!!editingId}
                          onClick={() => startEdit(r)}
                          data-testid={`${testIdPrefix}-edit-${r[uniqKey] || r.id}`}
                          className="h-8"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          disabled={deleting[r.id] || !!editingId}
                          onClick={() => remove(r)}
                          data-testid={`${testIdPrefix}-delete-${r[uniqKey] || r.id}`}
                          className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50"
                        >
                          {deleting[r.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="text-center text-slate-400 py-8">
                {query ? "No rows match your search." : "No rows yet — click Add to create one."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AccountMasterInner() {
  return (
    <Tabs defaultValue="gender" className="w-full">
      {/* Subtabs under the "Account Master" parent — slate/neutral pills
          matching the RTA subtabs so parent vs child hierarchy reads
          consistently across both branches of Masters. */}
      <TabsList data-testid="account-master-subtabs" className="bg-slate-100 h-9 gap-0.5">
        <TabsTrigger
          value="gender"
          data-testid="tab-gender"
          className="px-3 py-1 text-xs font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
        >
          <Users className="mr-1.5 h-3.5 w-3.5" /> Gender
        </TabsTrigger>
        <TabsTrigger
          value="country"
          data-testid="tab-country"
          className="px-3 py-1 text-xs font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
        >
          <Globe className="mr-1.5 h-3.5 w-3.5" /> Country
        </TabsTrigger>
      </TabsList>

      <TabsContent value="gender" className="mt-4">
        <MasterTable
          endpoint="gender"
          uniqKey="code"
          testIdPrefix="gender"
          newRowShape={{ code: "", name: "" }}
          columns={[
            { key: "code", label: "Code", maxLength: 2, placeholder: "M / F / O" },
            { key: "name", label: "Name", maxLength: 32, placeholder: "Male / Female / Other" },
          ]}
        />
      </TabsContent>

      <TabsContent value="country" className="mt-4">
        <MasterTable
          endpoint="country"
          uniqKey="code"
          testIdPrefix="country"
          newRowShape={{ code: "", name: "", country_code: 0 }}
          columns={[
            { key: "code", label: "Code (ISO Alpha-3)", maxLength: 3, placeholder: "IND" },
            { key: "name", label: "Name", maxLength: 96, placeholder: "India" },
            { key: "country_code", label: "Dial Code", type: "number", placeholder: "91" },
          ]}
        />
      </TabsContent>
    </Tabs>
  );
}

export default function AccountCreationMaster() {
  // Standalone page — kept for direct-URL access; the primary entry point
  // is now the "Account Master" tab under Masters (`/masters`).
  return (
    <div className="p-6" data-testid="account-creation-master-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Account Creation Master</h1>
        <p className="text-sm text-slate-500 mt-1">
          Reference tables used when creating a new investor / account. Seeded on first
          boot from the ISO 3166-1 Alpha-3 standard — edit as needed; changes persist
          across restarts.
        </p>
      </div>
      <AccountMasterInner />
    </div>
  );
}
